import { useState, useEffect } from 'react';
import {
  Paper,
  Typography,
  Box,
  CircularProgress,
  Alert,
  Card,
  CardContent,
  Divider,
  Chip,
  Grid,
  List,
  ListItem,
  ListItemText,
  Avatar,
  TextField,
  Button,
  IconButton,
  Tabs,
  Tab,
  LinearProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Menu,
  MenuItem
} from '@mui/material';
import SendIcon from '@mui/icons-material/Send';
import AttachFileIcon from '@mui/icons-material/AttachFile';
import DownloadIcon from '@mui/icons-material/Download';
import DeleteIcon from '@mui/icons-material/Delete';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import RefreshIcon from '@mui/icons-material/Refresh';
import EditIcon from '@mui/icons-material/Edit';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import { APIHOST } from '../../common';
import { useAuth } from '../auth/AuthContext';
import { Order } from '../../types/order';
import { OrderRoomOccupant } from '../../types/orderRoomOccupant';
import { OrderRoom } from '../../types/orderRoom';
import { Message } from '../../types/message';

import { formatDateFromYYYYMMDD } from '../../../lib/datetime';
import EditOrderContactDialog from '../dialog/EditOrderContactDialog';
import EditOrderBillingDialog from '../dialog/EditOrderBillingDialog';
import EditOrderRoomOccupantDialog from '../dialog/EditOrderRoomOccupantDialog';
import EditRoomDialog from '../dialog/EditRoomDialog';
import { useApp } from '../AppContext';
import { set } from 'date-fns';
import MessageStatus from '../MessageStatus';

// History interface
interface History {
  documentId: string;
  title: string;
  datetime: string;
  creatorType: 'system' | 'user' | 'internal';
  json?: any;
  author?: {
    documentId: string;
    firstName: string;
    lastName: string;
  };
  user?: {
    documentId: string;
    firstName: string;
    lastName: string;
  };
}

interface OrderDetailProps {
  orderId?: string;
}

export default function OrderDetail({ orderId }: OrderDetailProps) {
  const { currentUser,  refreshUnreadMessages} = useAuth();
  const [order, setOrder] = useState<Order | null>(null);
  const [orderMessages, setOrderMessages] = useState<Message[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [messagesLoading, setMessagesLoading] = useState(true);
  const [histories, setHistories] = useState<History[]>([]);
  const [historiesLoading, setHistoriesLoading] = useState(false);
  const [historiesError, setHistoriesError] = useState('');
  const [newMessage, setNewMessage] = useState('');
  const [sendingMessage, setSendingMessage] = useState(false);
  const [activeTab, setActiveTab] = useState(0);
  const [uploadingFile, setUploadingFile] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [regeneratingInvoice, setRegeneratingInvoice] = useState(false);
  const [gettingInvoiceSignedUrl, setGettingInvoiceSignedUrl] = useState(false);
  const [sendingInvoice, setSendingInvoice] = useState(false);
  const [expandedHistoryItems, setExpandedHistoryItems] = useState<Set<string>>(new Set());
  const [availableHotels, setAvailableHotels] = useState<any[]>([]);
  const [availableHotelsLoading, setAvailableHotelsLoading] = useState(false);
  const [availableHotelsError, setAvailableHotelsError] = useState('');
  
  // Delete room dialog state
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [roomToDelete, setRoomToDelete] = useState<OrderRoom | null>(null);
  const [deletingRoom, setDeletingRoom] = useState(false);

  // Edit dialogs state
  const [editContactDialogOpen, setEditContactDialogOpen] = useState(false);
  const [editBillingDialogOpen, setEditBillingDialogOpen] = useState(false);
  const [editOccupantDialogOpen, setEditOccupantDialogOpen] = useState(false);
  const [editRoomDialogOpen, setEditRoomDialogOpen] = useState(false);
  const [selectedOccupant, setSelectedOccupant] = useState<OrderRoomOccupant | null>(null);
  const [selectedRoom, setSelectedRoom] = useState<OrderRoom | null>(null);
  const [editingRoomId, setEditingRoomId] = useState<string | null>(null);

  // Room menu state
  const [roomMenuAnchor, setRoomMenuAnchor] = useState<HTMLElement | null>(null);
  const [selectedRoomIndex, setSelectedRoomIndex] = useState<number | null>(null);

  const { setSnackbarInput } = useApp();

  // Function to detect duplicate rooms
  const getDuplicateRooms = (rooms: OrderRoom[]) => {
    const duplicateIndexes = new Set<number>();
    
    for (let i = 0; i < rooms.length; i++) {
      for (let j = i + 1; j < rooms.length; j++) {
        const room1 = rooms[i];
        const room2 = rooms[j];
        
        // Check if rooms are duplicates based on type, dates, and occupants
        const sameType = room1.type === room2.type;
        const sameDates = room1.arrivalDate === room2.arrivalDate && room1.departureDate === room2.departureDate;
        
        // Compare occupants (same number and same names)
        const occupants1 = room1.order_room_occupants || [];
        const occupants2 = room2.order_room_occupants || [];
        
        let sameOccupants = occupants1.length === occupants2.length;
        if (sameOccupants && occupants1.length > 0) {
          // Sort occupants by name for comparison
          const sorted1 = occupants1.map(o => `${o.firstName} ${o.lastName}`).sort();
          const sorted2 = occupants2.map(o => `${o.firstName} ${o.lastName}`).sort();
          sameOccupants = sorted1.every((name, idx) => name === sorted2[idx]);
        }
        
        if (sameType && sameDates && sameOccupants) {
          // Only mark the later occurrence (j) as duplicate, not the first one (i)
          duplicateIndexes.add(j);
        }
      }
    }
    
    return duplicateIndexes;
  };

  // Delete room functions
  const handleDeleteRoom = (room: OrderRoom) => {
    setRoomToDelete(room);
    setDeleteDialogOpen(true);
  };

  const handleCloseDeleteDialog = () => {
    setDeleteDialogOpen(false);
    setRoomToDelete(null);
  };

  const handleConfirmDelete = async () => {
    if (!roomToDelete?.documentId) return;
    
    setDeletingRoom(true);
    try {
      const token = localStorage.getItem('authToken');
      if (!token) {
        throw new Error('No authentication token found');
      }

      const response = await fetch(`${APIHOST}/api/order-rooms/${roomToDelete.documentId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });

      if (response.ok) {
        setSnackbarInput({ message: 'Room deleted successfully', severity: 'success' });
        handleCloseDeleteDialog();
        // Refresh the order data
        await fetchOrder();
      } else {
        throw new Error('Failed to delete room');
      }
    } catch (error) {
      console.error('Error deleting room:', error);
      setSnackbarInput({ message: 'Failed to delete room', severity: 'error' });
    } finally {
      setDeletingRoom(false);
    }
  };

  const handleEditOccupant = (occupant: OrderRoomOccupant, room: OrderRoom) => {
    setSelectedOccupant(occupant);
    setSelectedRoom(room);
    setEditOccupantDialogOpen(true);
  };

  const handleDeleteOccupant = async (occupant: OrderRoomOccupant) => {
    if (!window.confirm(`Are you sure you want to delete ${occupant.firstName} ${occupant.lastName}?`)) {
      return;
    }

    try {
      const token = localStorage.getItem('authToken');
      if (!token) {
        throw new Error('No authentication token found');
      }

      const response = await fetch(`${APIHOST}/api/order-room-occupants/${occupant.documentId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        throw new Error('Failed to delete occupant');
      }

      setSnackbarInput({ message: 'Occupant deleted successfully', severity: 'success' });
      // Refresh order data
      await fetchOrder();
    } catch (error) {
      console.error('Error deleting occupant:', error);
      setSnackbarInput({ message: 'Failed to delete occupant', severity: 'error' });
    }
  };

  // Room menu handlers
  const handleRoomMenuOpen = (event: React.MouseEvent<HTMLElement>, index: number) => {
    setRoomMenuAnchor(event.currentTarget);
    setSelectedRoomIndex(index);
  };

  const handleRoomMenuClose = () => {
    setRoomMenuAnchor(null);
    setSelectedRoomIndex(null);
  };

  const handleAddOccupant = (room: OrderRoom) => {
    setSelectedOccupant(null); // null means add new occupant
    setSelectedRoom(room);
    setEditOccupantDialogOpen(true);
    handleRoomMenuClose();
  };

  const handleDeleteRoomFromMenu = (room: OrderRoom) => {
    handleDeleteRoom(room);
    handleRoomMenuClose();
  };

  const handleEditRoom = (room: OrderRoom) => {
    setEditingRoomId(room.documentId || room.id);
    setEditRoomDialogOpen(true);
    handleRoomMenuClose();
  };

  useEffect(() => {
    if (orderId) {
      fetchOrder();
      fetchOrderMessages();
      const interval = setInterval(fetchOrderMessages, 60000);
      return () => clearInterval(interval);
    }
  }, [orderId]);

  const fetchOrder = async () => {
    if (!orderId) return;
    
    try {
      setLoading(true);
      setError('');

      const token = localStorage.getItem('authToken');
      if (!token) {
        throw new Error('No authentication token found');
      }

      const response = await fetch(
        `${APIHOST}/api/orders/${orderId}?populate[]=conference_group&populate[]=conference_hotel.hotel&populate[]=conference_hotel.conference&populate[]=order_rooms.order_room_occupants&populate[]=user_files.file&populate[]=user_files.user&populate[]=user_files.message&populate[]=history.author`,
        {
          headers: {
            'Authorization': `Bearer ${token}`,
          },
        }
      );

      if (!response.ok) {
        throw new Error('Failed to fetch order details');
      }

      const data = await response.json();
      setOrder(data.data);

    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load order');
      console.error('Order detail error:', err);
    } finally {
      setLoading(false);
    }
  };

  const fetchOrderMessages = async () => {
    if (!orderId) return;
    
    try {
      const token = localStorage.getItem('authToken');
      if (!token) {
        throw new Error('No authentication token found');
      }

      const response = await fetch(
        `${APIHOST}/api/messages?filters[order][documentId][$eq]=${orderId}&populate[]=author&populate[]=seenBy&sort=createdAt:asc`,
        {
          headers: {
            'Authorization': `Bearer ${token}`,
          },
        }
      );

      if (!response.ok) {
        throw new Error('Failed to fetch messages');
      }

      const data = await response.json();
      setOrderMessages(data.data || []);

    } catch (err) {
      setSnackbarInput({ message: err instanceof Error ? err.message : 'Failed to load messages', severity: 'error' });
      console.error('Messages error:', err);
    } finally {
      setMessagesLoading(false);
    }
  };

  const sendMessage = async () => {
    if (!newMessage.trim() || !orderId) return;
    
    try {
      setSendingMessage(true);
      
      const token = localStorage.getItem('authToken');
      if (!token) {
        throw new Error('No authentication token found');
      }

      const response = await fetch(
        `${APIHOST}/api/messages`,
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            data: {
              message: newMessage.trim(),
              orderId: orderId
            }
          })
        }
      );

      if (!response.ok) {
        throw new Error('Failed to send message');
      }
      setSnackbarInput({ message: 'Message sent successfully', severity: 'success' });
      setNewMessage('');
      await fetchOrderMessages(); // Refresh messages

    } catch (err) {
      console.error('Error sending message:', err);
      setSnackbarInput({ message: 'Failed to send message', severity: 'error' });
    } finally {
      setSendingMessage(false);
    }
  };

  const fetchHistories = async () => {
    if (!orderId) return;
    
    try {
      setHistoriesLoading(true);
      setHistoriesError('');

      const token = localStorage.getItem('authToken');
      if (!token) {
        throw new Error('No authentication token found');
      }

      const response = await fetch(
        `${APIHOST}/api/histories?filters[order][documentId][$eq]=${orderId}&populate[0]=author&populate[1]=user&sort=createdAt:desc`,
        {
          headers: {
            'Authorization': `Bearer ${token}`,
          },
        }
      );

      if (!response.ok) {
        throw new Error('Failed to fetch histories');
      }

      const data = await response.json();
      setHistories(data.data || []);

    } catch (err) {
      setSnackbarInput({ message: err instanceof Error ? err.message : 'Failed to load histories', severity: 'error' });
      console.error('Histories error:', err);
    } finally {
      setHistoriesLoading(false);
    }
  };

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setActiveTab(newValue);
    
    // Fetch histories when history tab is selected
    if (newValue === 2) {
      fetchHistories();
    }
    
    // Fetch available hotels when available tab is selected
    if (newValue === 3) {
      fetchAvailableHotels();
    }
  };

  const fetchAvailableHotels = async () => {
    if (!order?.documentId) return;
    
    setAvailableHotelsLoading(true);
    setAvailableHotelsError('');
    
    try {
      const token = localStorage.getItem('authToken');
      const response = await fetch(
        `${APIHOST}/api/conference-hotels/available?orderId=${order.documentId}`,
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
        }
      );
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      const data = await response.json();
      setAvailableHotels(data.data || []);
    } catch (error) {
      console.error('Error fetching available hotels:', error);
      setSnackbarInput({ message: 'Failed to load available hotels', severity: 'error' });
    } finally {
      setAvailableHotelsLoading(false);
    }
  };

  const toggleHistoryJson = (historyId: string) => {
    setExpandedHistoryItems(prev => {
      const newSet = new Set(prev);
      if (newSet.has(historyId)) {
        newSet.delete(historyId);
      } else {
        newSet.add(historyId);
      }
      return newSet;
    });
  };

  const handleFileUpload = async (file: File) => {
    if (!orderId || !order) return;

    try {
      setUploadingFile(true);
      setUploadProgress(0);
      
      const token = localStorage.getItem('authToken');
      if (!token) {
        throw new Error('No authentication token found');
      }

      const formData = new FormData();
      formData.append('files', file);
      formData.append('order', order.documentId);

      const xhr = new XMLHttpRequest();
      
      xhr.upload.addEventListener('progress', (e) => {
        if (e.lengthComputable) {
          const progress = (e.loaded / e.total) * 100;
          setUploadProgress(progress);
        }
      });

      xhr.open('POST', `${APIHOST}/api/file-uploads`);
      xhr.setRequestHeader('Authorization', `Bearer ${token}`);

      xhr.onload = function() {
        if (xhr.status === 200) {
          // Refresh the order to get updated files
          fetchOrder();
        } else {
          throw new Error('Failed to upload file');
        }
      };

      xhr.onerror = function() {
        throw new Error('Failed to upload file');
      };

      xhr.send(formData);
      setSnackbarInput({ message: 'File uploaded successfully', severity: 'success' });
    } catch (err) {
      console.error('Error uploading file:', err);
      setSnackbarInput({ message: 'Failed to upload file. Please try again.', severity: 'error' });
    } finally {
      setUploadingFile(false);
      setUploadProgress(0);
      
    }
  };

  const handleFileDownload = async (documentId: string, fileName: string) => {
    try {
      const token = localStorage.getItem('authToken');
      if (!token) {
        throw new Error('No authentication token found');
      }

      const response = await fetch(`${APIHOST}/api/file-uploads/getUrl/${documentId}`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        throw new Error('Failed to get download URL');
      }

      const data = await response.json();
      
      // Create a temporary anchor element to trigger download
      const link = document.createElement('a');
      link.href = data.url;
      link.download = data.fileName || fileName;
      link.target = '_blank';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

    } catch (err) {
      console.error('Error downloading file:', err);
      setError('Failed to download file. Please try again.');
    }
  };

  const handleFileDelete = async (fileId: string) => {
    try {
      const token = localStorage.getItem('authToken');
      if (!token) {
        throw new Error('No authentication token found');
      }

      const response = await fetch(`${APIHOST}/api/user-files/${fileId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        throw new Error('Failed to delete file');
      }

      // Refresh the order to get updated files
      await fetchOrder();
      setSnackbarInput({ message: 'File deleted successfully', severity: 'success' });
    } catch (err) {
      console.error('Error deleting file:', err);
      setSnackbarInput({ message: 'Failed to delete file', severity: 'error' });
    }
  };

  const handleRegenerateInvoice = async () => {
    if (!orderId) return;

    try {
      setRegeneratingInvoice(true);
      
      const token = localStorage.getItem('authToken');
      if (!token) {
        throw new Error('No authentication token found');
      }

      const response = await fetch(`${APIHOST}/api/orders/${orderId}/genInvoice`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        throw new Error('Failed to generate invoice');
      }

      const data = await response.json();
      
      if (data.success) {
        // Refresh the order to get updated files
        await fetchOrder();
        // Show success message
        console.log('Invoice regenerated successfully:', data.fileName);
        setSnackbarInput({ message: 'Invoice regenerated successfully', severity: 'success' });
      } else {
        throw new Error(data.error || 'Failed to regenerate invoice');
      }

    } catch (err) {
      console.error('Error regenerating invoice:', err);
      setSnackbarInput({ message: 'Failed to regenerate invoice', severity: 'error' });
    } finally {
      setRegeneratingInvoice(false);
    }
  };

  const handleOpenInvoice = async () => {
    if (!order?.documentId) return;

    try {
      const token = localStorage.getItem('authToken');
      if (!token) {
        throw new Error('No authentication token found');
      }

      const response = await fetch(`${APIHOST}/api/orders/${order.documentId}/getInvoiceUrl`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        throw new Error('Failed to get invoice URL');
      }

      const data = await response.json();
      
      if (data.success && data.url) {
        // Open invoice in new tab for viewing
        window.open(data.url, '_blank');
      } else {
        throw new Error('Invalid response from server');
      }

    } catch (err) {
      console.error('Error opening invoice:', err);
      setSnackbarInput({ message: 'Failed to open invoice', severity: 'error' });
    }
  }

  const handleSendInvoice = async () => {
    if (!order?.documentId) return;
    
    try {
      setSendingInvoice(true);
      
      const token = localStorage.getItem('authToken');
      if (!token) {
        throw new Error('No authentication token found');
      }
      
      const response = await fetch(`${APIHOST}/api/orders/${order.documentId}/sendInvoice`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error?.message || 'Failed to send invoice');
      }
      
      setSnackbarInput({
        message: 'Invoice sent successfully',
        severity: 'success'
      });
      
    } catch (err) {
      console.error('Error sending invoice:', err);
      setSnackbarInput({
        message: 'Failed to send invoice',
        severity: 'error'
      });
    } finally {
      setSendingInvoice(false);
    }
  };

  const formatDateTime = (dateString: string) => {
    return new Date(dateString).toLocaleString();
  };

  if (!currentUser) {
    return (
      <Paper sx={{ p: 3, textAlign: 'center' }}> 
        <Typography variant="body1">You must be logged in to view order details.</Typography>
      </Paper>
    )
  }

  if (loading) {
    return (
      <Paper sx={{ p: 3, textAlign: 'center' }}>
        <CircularProgress sx={{ mb: 2 }} />
        <Typography variant="body1">Loading order details...</Typography>
      </Paper>
    );
  }

  if (error) {
    return (
      <Paper sx={{ p: 3 }}>
        <Alert severity="error">{error}</Alert>
      </Paper>
    );
  }

  if (!order) {
    return (
      <Paper sx={{ p: 3 }}>
        <Alert severity="warning">Order not found</Alert>
      </Paper>
    );
  }

  return (
    <Grid container sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
      {/* Order Header */}
      <Paper sx={{ p: 3 }}>
        <Box sx={{float: 'right' }}>
          <Typography variant="body2" color="text.secondary">
            <strong>Created:</strong> {formatDateTime(order.createdAt)}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            <strong>Updated:</strong> {formatDateTime(order.updatedAt)}
          </Typography>
          <br/>
          <Chip 
            label={order.orderStatus || 'Active'} 
            color="primary" 
            size="small" 
          />
        </Box>
        <Typography variant="h6" gutterBottom>
          Order Details
        </Typography>
        <Typography variant="body2">
          {order.conference_hotel?.conference.longName}
        </Typography>
        <Typography variant="body2" color="text.secondary" gutterBottom>
          Confirmation: {order.confirmation} 
        </Typography>
        
       <Divider sx={{ my: 3 }} />

        {/* Generate Invoice Button */}
        <Typography variant="h6" gutterBottom>
          Invoicing
        </Typography>
        <Box sx={{ display: 'flex', gap: 2, flexDirection: 'row', justifyContent: 'space-between' }}>
          <Box sx={{ mt: 2, mb: 2 }}>
            <Button
              variant="contained"
              color="primary"
              onClick={handleRegenerateInvoice}
              disabled={regeneratingInvoice || !order || !['received'].includes(order.orderStatus)}
              startIcon={regeneratingInvoice ? <CircularProgress size={20} /> : undefined}
            >
              {regeneratingInvoice ? 'Generating...' : 'Generate Invoice'}
            </Button>
            {order.invoiceGenerated && (
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                Invoice generated at {formatDateTime(order.invoiceGenerated)}
              </Typography>
            )}
          </Box>

          <Box sx={{ mt: 2, mb: 2 }}>
            <Button
              variant="contained"
              color="primary"
              onClick={handleOpenInvoice}
              disabled={!order.invoicePath}
              startIcon={gettingInvoiceSignedUrl ? <CircularProgress size={20} /> : undefined}
            >
              {'View Invoice'}
            </Button>
          </Box>

          <Box sx={{ mt: 2, mb: 2 }}>
            <Button
              variant="contained"
              color="primary"
              onClick={handleSendInvoice}
              disabled={!order.invoicePath || sendingInvoice}
              startIcon={sendingInvoice ? <CircularProgress size={20} /> : undefined}
            >
              {'Send Invoice'}
            </Button>
            {order.invoiceSent && (
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                Invoice sent at {formatDateTime(order.invoiceSent)}
              </Typography>
            )}
          </Box>
        </Box>

       

              <Divider sx={{ my: 3 }} />

              {/* Hotel Information */}
              {order.conference_hotel?.hotel ? (
                <Box sx={{ display: 'grid', gap: 1 }}>
                  <Typography variant="body2">
                    <strong>Hotel:</strong> {order.conference_hotel.hotel.longName}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {order.conference_hotel.hotel.addressCity}, {order.conference_hotel.hotel.addressState} {order.conference_hotel.hotel.addressZip}
                  </Typography>
                  {order.rewardsNumber && (
                    <Typography variant="body2">
                      <strong>Rewards:</strong> {order.rewardsNumber}
                    </Typography>
                  )}
                  
                      <Box sx={{ mt: 3 }}>
                        <Typography variant="body2">
                          <strong>Notes for Hotel:</strong>
                        </Typography>
                        <Typography variant="body2">
                          {order.notesForHotel || 'None'}
                        </Typography>
                      </Box>
               
                </Box>
              ) : (
                <Typography variant="body2" color="text.secondary">
                  No hotel selected
                </Typography>
              )}
      </Paper>

      

         

      <Grid container spacing={3}>
        {/* Conference Group Information */}
        <Grid size={{xs:12, md:6}}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Conference Group
              </Typography>
              <Box sx={{ display: 'grid', gap: 1 }}>
                <Typography variant="body2">
                  <strong>Group Name:</strong> {order.conference_group?.name || order.conference_group?.longName || 'Unknown'}
                </Typography>
                <Typography variant="body2">
                  <strong>Access Code:</strong> {order.accessCode || 'Unknown'}
                </Typography>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        {/* Contact Information */}
        <Grid size={{xs:12, md:6}}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Typography variant="h6">
                  Contact Information
                </Typography>
                <IconButton 
                  size="small" 
                  onClick={() => setEditContactDialogOpen(true)}
                  sx={{ color: 'primary.main' }}
                >
                  <EditIcon fontSize="small" />
                </IconButton>
              </Box>
              <Box sx={{ display: 'grid', gap: 1 }}>
                <Typography variant="body2">
                  <strong>Name:</strong> {order.contactFirstName} {order.contactLastName}
                </Typography>
                <Typography variant="body2">
                  <strong>Email:</strong> {order.contactEmail}
                </Typography>
                <Typography variant="body2">
                  <strong>Phone:</strong> {order.contactCell || 'Not provided'}
                </Typography>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        {/* Billing Information */}
        <Grid size={{xs:12, md:6}}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Typography variant="h6">
                  Billing Information
                </Typography>
                <IconButton 
                  size="small" 
                  onClick={() => setEditBillingDialogOpen(true)}
                  sx={{ color: 'primary.main' }}
                >
                  <EditIcon fontSize="small" />
                </IconButton>
              </Box>
              <Box sx={{ display: 'grid', gap: 1 }}>
                <Typography variant="body2">
                  <strong>Name:</strong> {order.billingAddressee}
                </Typography>
                <Typography variant="body2">
                  <strong>Address:</strong> {order.billingStreet1}
                  {order.billingStreet2 && `, ${order.billingStreet2}`}
                </Typography>
                <Typography variant="body2">
                  <strong>City:</strong> {order.billingCity}, {order.billingState} {order.billingZip}
                </Typography>
                <Typography variant="body2">
                  <strong>Country:</strong> {order.billingCountry}
                </Typography>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        {/* Transportation */}
        <Grid size={{xs:12, md:6}}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Transportation
              </Typography>
              <Box sx={{ display: 'grid', gap: 1 }}>
                <Typography variant="body2">
                  <strong>Bus Parking:</strong> {order.requiresBusParking ? 'Required' : 'Not required'}
                </Typography>
                <Typography variant="body2">
                  <strong>Transit to Venue:</strong> {order.requiresTransitToVenue ? 'Required' : 'Not required'}
                </Typography>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        {/* Order Details */}
        <Grid size={{xs:12, md:6}}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'grid', gap: 1 }}>
                <Typography variant="body2">
                  <strong>Students Share Beds:</strong> {order.studentsShareBeds ? 'Yes' : 'No'}
                </Typography>
                {order.termsAccepted && (
                  <Typography variant="body2">
                    <strong>Terms Accepted:</strong> Yes
                  </Typography>
                )}
              </Box>
            </CardContent>
          </Card>
        </Grid>

        {/* Rooms and Occupants */}
        <Grid size={{xs:12}}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Rooms & Occupants ({order.order_rooms?.length || 0} rooms)
              </Typography>
              {order.order_rooms && order.order_rooms.length > 0 ? (
                (() => {
                  const duplicateIndexes = getDuplicateRooms(order.order_rooms);
                  return order.order_rooms.map((room: OrderRoom, index) => (
                    <Box 
                      key={index} 
                      sx={{ 
                        mb: 3, 
                        p: 2, 
                        border: '1px solid', 
                        borderColor: duplicateIndexes.has(index) ? 'warning.main' : 'grey.300',
                        backgroundColor: duplicateIndexes.has(index) ? 'warning.50' : 'transparent',
                        borderRadius: 1,
                        position: 'relative'
                      }}
                    >
                      {duplicateIndexes.has(index) && (
                        <Chip 
                          label="Potential Duplicate" 
                          color="warning" 
                          size="small" 
                          sx={{ 
                            position: 'absolute', 
                            top: 18, 
                            right: 50,
                            fontSize: '0.75rem'
                          }} 
                        />
                      )}
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <Typography variant="subtitle1" fontWeight="600">
                          Room {index + 1}
                        </Typography>
                        <IconButton 
                          size="small"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleRoomMenuOpen(e, index);
                          }}
                          sx={{ ml: 1 }}
                        >
                          <MoreVertIcon fontSize="small" />
                        </IconButton>
                      </Box>
                    <Box sx={{ mt: 1, display: 'grid', gap: 1 }}>
                      <Typography variant="body2">
                        <strong>Type:</strong> {room.type || 'Not specified'}
                      </Typography>
                      <Typography variant="body2">
                        <strong>Check-in:</strong> {formatDateFromYYYYMMDD(room.arrivalDate)}
                      </Typography>
                      <Typography variant="body2">
                        <strong>Check-out:</strong> {formatDateFromYYYYMMDD(room.departureDate)}
                      </Typography>
                      {room.order_room_occupants && room.order_room_occupants.length > 0 && (
                        <>
                          <Typography variant="body2" sx={{ mt: 1, mb: 1 }}>
                            <strong>Occupants ({room.order_room_occupants.length}):</strong>
                          </Typography>
                          {room.order_room_occupants.map((occupant: OrderRoomOccupant, occupantIndex: number) => (
                            <Box 
                              key={occupantIndex}
                              sx={{ 
                                display: 'flex', 
                                justifyContent: 'space-between', 
                                alignItems: 'center',
                                py: 0.5,
                                px: 1,
                                border: '1px solid',
                                borderColor: 'divider',
                                borderRadius: 1,
                                mb: 0.5,
                                bgcolor: 'background.paper'
                              }}
                            >
                              <Typography variant="body2">
                                {occupant.firstName} {occupant.lastName}
                              </Typography>
                              <Box sx={{ display: 'flex', gap: 0.5 }}>
                                <IconButton
                                  size="small"
                                  onClick={() => handleEditOccupant(occupant, room)}
                                  sx={{ color: 'primary.main' }}
                                >
                                  <EditIcon fontSize="small" />
                                </IconButton>
                                <IconButton
                                  size="small"
                                  onClick={() => handleDeleteOccupant(occupant)}
                                  sx={{ color: 'error.main' }}
                                >
                                  <DeleteIcon fontSize="small" />
                                </IconButton>
                              </Box>
                            </Box>
                          ))}
                        </>
                      )}
                    </Box>
                  </Box>
                ))
                })()
              ) : (
                <Typography variant="body2" color="text.secondary">
                  No rooms found for this order
                </Typography>
              )}
              
              
            </CardContent>
          </Card>
        </Grid>

        {/* Messages, Files, and History Tabs */}
        <Grid size={{xs:12}}>
          <Card>
            <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
              <Tabs value={activeTab} onChange={handleTabChange}>
                <Tab label="Messages" />
                <Tab label="Files" />
                <Tab label="History" />
                <Tab label="Availability" />
              </Tabs>
            </Box>
            
            {/* Messages Tab */}
            {activeTab === 0 && (
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Messages ({orderMessages.length})
                </Typography>
                {messagesLoading || !orderMessages ? (
                  <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
                    <CircularProgress size={24} />
                    <Typography variant="body2" sx={{ ml: 2 }}>
                      No Messages
                    </Typography>
                  </Box>
                 ): (
                  <Box sx={{ display: 'flex', flexDirection: 'column-reverse',overflowY: 'auto', height: '400px', mb: 3 }}>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                      {orderMessages.map((message: Message) => {
                        const isInternal = currentUser && message.author.userType === 'internal';
                        const isCurrentUser = currentUser && message.author.documentId === currentUser.documentId;
                    
                        return (
                          <Box 
                            key={message.documentId} 
                            sx={{ 
                              display: 'flex',
                              justifyContent: isCurrentUser ? 'flex-start' : 'flex-end'
                            }}
                          >
                            <Card 
                              variant="outlined" 
                              sx={{ 
                                p: 2,
                                maxWidth: '75%',
                                bgcolor: isCurrentUser ? 'primary.50' : 'background.paper'
                              }}
                            >
                              <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2, flexDirection: 'row' }}>
                                <Avatar sx={{ width: 36, height: 36 }}>
                                  {message.author?.firstName?.charAt(0) || message.author?.email?.charAt(0) || 'U'}
                                </Avatar>
                                <Box sx={{ flexGrow: 1 }}>
                                  <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1, mb: 1, flexDirection: 'column' }}>
                                    <Typography variant="body2" fontWeight="medium">
                                      {message.author?.firstName && message.author?.lastName 
                                        ? `${message.author.firstName} ${message.author.lastName}`
                                        : message.author?.email || 'Unknown User'
                                      }
                                    </Typography>
                                    {message.author?.userType && (
                                          <Chip 
                                          label={message.author.userType} 
                                          size="small"
                                          color={isInternal ? "primary" : "default"}
                                            sx={{ height: 20 }}
                                        />
                                      )}
                                    <MessageStatus message={message} user={currentUser} onUpdate={() => {refreshUnreadMessages; fetchOrderMessages()}}/>
                                  </Box>
                                  <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', mb: 1, textAlign: isInternal ? 'left' : 'right' }}>
                                    {message.message}
                                  </Typography>
                                </Box>
                              </Box>
                            </Card>
                          </Box>
                        );
                      })}
                    </Box>
                  </Box>
                )}
                
                {/* New Message Form */}
                <Divider sx={{ my: 2 }} />
                <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-end' }}>
                  <TextField
                    label="Add a message"
                    multiline
                    maxRows={4}
                    value={newMessage}
                    onChange={(e) => setNewMessage(e.target.value)}
                    onKeyPress={(e) => {
                      if (e.key === 'Enter' && !e.shiftKey) {
                        e.preventDefault();
                        sendMessage();
                      }
                    }}
                    fullWidth
                    variant="outlined"
                    size="small"
                    disabled={sendingMessage}
                    placeholder="Type your message here..."
                  />
                  <IconButton
                    onClick={sendMessage}
                    disabled={!newMessage.trim() || sendingMessage}
                    color="primary"
                    size="large"
                  >
                    {sendingMessage ? <CircularProgress size={24} /> : <SendIcon />}
                  </IconButton>
                </Box>
              </CardContent>
            )}

            {/* Files Tab */}
            {activeTab === 1 && (
              <CardContent>
                <Box sx={{ mb: 3 }}>
                  <Typography variant="h6" gutterBottom>
                    Upload Files
                  </Typography>
                  <input
                    accept="*/*"
                    style={{ display: 'none' }}
                    id="file-upload-input-admin"
                    type="file"
                    onChange={(e) => {
                      const file = e.target.files?.[0];
                      if (file) {
                        handleFileUpload(file);
                        // Reset the input
                        e.target.value = '';
                      }
                    }}
                  />
                  <label htmlFor="file-upload-input-admin">
                    <Button
                      variant="outlined"
                      component="span"
                      startIcon={<AttachFileIcon />}
                      disabled={uploadingFile}
                    >
                      Choose File
                    </Button>
                  </label>
                  {uploadingFile && (
                    <Box sx={{ mt: 2 }}>
                      <Typography variant="body2" color="text.secondary" gutterBottom>
                        Uploading... {Math.round(uploadProgress)}%
                      </Typography>
                      <LinearProgress variant="determinate" value={uploadProgress} />
                    </Box>
                  )}
                </Box>

                <Divider sx={{ my: 2 }} />

                <Typography variant="h6" gutterBottom>
                  Uploaded Files
                </Typography>
                {order?.user_files && order.user_files.length > 0 ? (
                  <List>
                    {order.user_files.map((userFile, index) => {
                      const file = userFile.file;
                      if (!file) return null;
                      
                      return (
                        <Box key={userFile.id}>
                          <ListItem
                            sx={{ px: 0 }}
                            secondaryAction={
                              <Box>
                                <IconButton
                                  edge="end"
                                  aria-label="download"
                                  onClick={() => handleFileDownload(userFile.documentId, userFile.title)}
                                  sx={{ ml: 1 }}
                                >
                                  <DownloadIcon />
                                </IconButton>
                                <IconButton
                                  edge="end"
                                  aria-label="delete"
                                  onClick={() => handleFileDelete(userFile.documentId)}
                                  color="error"
                                >
                                  <DeleteIcon />
                                </IconButton>
                              </Box>
                            }
                          >
                            <ListItemText
                              primary={userFile.title || file.name}
                              secondary={
                                <Box>
                                  <Typography variant="body1" color="text.secondary">
                                    {userFile.user?.firstName && userFile.user?.lastName
                                      ? `Uploaded by ${userFile.user.firstName} ${userFile.user.lastName}`
                                      : 'Unknown uploader'
                                    }
                                  </Typography>
                                  <Typography variant="body1" color="text.secondary">
                                    Size: {(file.size / 1024).toFixed(1)} KB
                                  </Typography>
                                  <Typography variant="body1" color="text.secondary">
                                    Uploaded: {formatDateTime(userFile.createdAt)}
                                  </Typography>
                                </Box>
                              }
                            />
                          </ListItem>
                          {index < order.user_files!.length - 1 && <Divider />}
                        </Box>
                      );
                    })}
                  </List>
                ) : (
                  <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                    No files uploaded
                  </Typography>
                )}
              </CardContent>
            )}

            {/* History Tab */}
            {activeTab === 2 && (
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Order History
                </Typography>
                {historiesLoading ? (
                  <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
                    <CircularProgress />
                  </Box>
                ) : historiesError ? (
                  <Alert severity="error" sx={{ mb: 2 }}>
                    {historiesError}
                  </Alert>
                ) : histories && histories.length > 0 ? (
                  <List>
                    {histories.map((history, index) => (
                      <Box key={history.documentId}>
                        <ListItem sx={{ px: 0, alignItems: 'flex-start' }}>
                          <ListItemText
                            primary={
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <Typography variant="subtitle2">
                                  {history.title}
                                </Typography>
                                <Chip 
                                  label={history.creatorType} 
                                  size="small" 
                                  color={history.creatorType === 'system' ? 'default' : 'primary'}
                                />
                                {history.json && (
                                  <IconButton 
                                    size="small" 
                                    onClick={() => toggleHistoryJson(history.documentId)}
                                    sx={{ ml: 'auto' }}
                                  >
                                    {expandedHistoryItems.has(history.documentId) ? 
                                      <ExpandLessIcon fontSize="small" /> : 
                                      <ExpandMoreIcon fontSize="small" />
                                    }
                                  </IconButton>
                                )}
                              </Box>
                            }
                            secondary={
                              <Box sx={{ mt: 1 }}>
                                <Typography variant="body2" color="text.secondary">
                                  {formatDateTime(history.datetime)}
                                </Typography>
                                {history.author && (
                                  <Typography variant="body2" color="text.secondary">
                                    By: {history.author.firstName} {history.author.lastName}
                                  </Typography>
                                )}
                                {history.user && history.user.documentId !== history.author?.documentId && (
                                  <Typography variant="body2" color="text.secondary">
                                    User: {history.user.firstName} {history.user.lastName}
                                  </Typography>
                                )}
                                {history.json && expandedHistoryItems.has(history.documentId) && (
                                  <Box sx={{ mt: 1, p: 1, bgcolor: 'grey.50', borderRadius: 1 }}>
                                    <Typography variant="caption" color="text.secondary" component="pre">
                                      {JSON.stringify(history.json, null, 2)}
                                    </Typography>
                                  </Box>
                                )}
                              </Box>
                            }
                          />
                        </ListItem>
                        {index < histories.length - 1 && <Divider />}
                      </Box>
                    ))}
                  </List>
                ) : (
                  <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                    No history found
                  </Typography>
                )}
              </CardContent>
            )}

            {/* Available Hotels Tab */}
            {activeTab === 3 && (
              <CardContent>
                 <Typography variant="h6" gutterBottom>
                    Room Requirements
                  </Typography>
                  <Typography variant="body2" color="text.secondary" gutterBottom>
                    These are the details provided by the user on the room requirements screen.
                  </Typography>
                <Box sx={{ mb: 3, pl: 3, borderLeft: '10px solid', borderColor: 'primary.main', bgcolor: 'primary.50' }}>
                  {/* Order Details Section */}
                  <Grid container spacing={2} mt={2}>
                    <Grid size={{xs:12, sm:6}}>
                      <Typography variant="body2" color="text.secondary">
                        <strong>Arrival Date:</strong> {order?.selectedArrivalDate ? formatDateFromYYYYMMDD(order.selectedArrivalDate) : 'Not specified'}
                      </Typography>
                    </Grid>
                    <Grid size={{xs:12, sm:6}}>
                      <Typography variant="body2" color="text.secondary">
                        <strong>Departure Date:</strong> {order?.selectedDepartureDate ? formatDateFromYYYYMMDD(order.selectedDepartureDate) : 'Not specified'}
                      </Typography>
                    </Grid>
                    <Grid size={{xs:12, sm:6}}>
                      <Typography variant="body2" color="text.secondary">
                        <strong>One Bedroom Rooms:</strong> {order?.oneBedRoomsNeeded || 0}
                      </Typography>
                    </Grid>
                    <Grid size={{xs:12, sm:6}}>
                      <Typography variant="body2" color="text.secondary">
                        <strong>Two Bedroom Rooms:</strong> {order?.twoBedRoomsNeeded || 0}
                      </Typography>
                    </Grid>
                  </Grid>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                  <Typography variant="h6">
                    Matching Hotels
                  </Typography>
                  <IconButton 
                    size="small" 
                    onClick={fetchAvailableHotels}
                    disabled={availableHotelsLoading}
                    title="Refresh available hotels"
                  >
                    <RefreshIcon fontSize="small" />
                  </IconButton>
                </Box>
                <Typography variant="body2" color="text.secondary" gutterBottom>
                    This is the list of hotels that have availability matching the order's room requirements that the user would see right now. This does not reflect what the user saw in the past.
                  </Typography>
                  <Typography variant="body2" gutterBottom fontWeight={900}>
                    Note: Check history tab for details on why other hotels for this conference may not have been shown to the user based on their requirements. Look for the word 'disqualified' in the history entries.
                  </Typography>
                <Box sx={{ my: 3, pl: 3, borderLeft: '10px solid', borderColor: 'primary.main', bgcolor: 'primary.50' }}>
                  
                  {availableHotelsLoading ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
                      <CircularProgress />
                    </Box>
                  ) : availableHotelsError ? (
                    <Alert severity="error" sx={{ mb: 2 }}>
                      {availableHotelsError}
                    </Alert>
                  ) : availableHotels && availableHotels.length > 0 ? (
                    <List>
                      {availableHotels.map((hotel, index) => (
                        <Box key={hotel.documentId || index}>
                          <ListItem sx={{ px: 0, alignItems: 'flex-start' }}>
                            <ListItemText
                              primary={
                                <Typography variant="subtitle1">
                                  {hotel.hotel?.name || hotel.name || 'Unnamed Hotel'}
                                </Typography>
                              }
                              secondary={
                                <Box sx={{ mt: 1 }}>
                                  {hotel.hotel?.address && (
                                    <Typography variant="body2" color="text.secondary">
                                      {hotel.hotel.address}
                                    </Typography>
                                  )}
                                  {hotel.available_rooms && (
                                    <Typography variant="body2" color="primary">
                                      Available Rooms: {hotel.available_rooms}
                                    </Typography>
                                  )}
                                  {hotel.conference && (
                                    <Typography variant="body2" color="text.secondary">
                                      Conference: {hotel.conference?.name || hotel.conference?.longName}
                                    </Typography>
                                  )}
                                </Box>
                              }
                            />
                          </ListItem>
                          {index < availableHotels.length - 1 && <Divider />}
                        </Box>
                      ))}
                    </List>
                  ) : (
                    <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                      No available hotels found
                    </Typography>
                )}
                </Box>
              </CardContent>
            )}
          </Card>
        </Grid>
      </Grid>
      
      {/* Delete Room Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onClose={handleCloseDeleteDialog}>
        <DialogTitle>Delete Room</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete this room? This action cannot be undone.
          </Typography>
          {roomToDelete && (
            <Box sx={{ mt: 2, p: 2}}>
              <Typography variant="body2"><strong>Type:</strong> {roomToDelete.type}</Typography>
              <Typography variant="body2"><strong>Check-in:</strong> {formatDateFromYYYYMMDD(roomToDelete.arrivalDate)}</Typography>
              <Typography variant="body2"><strong>Check-out:</strong> {formatDateFromYYYYMMDD(roomToDelete.departureDate)}</Typography>
              <Typography variant="body2"><strong>Occupants ({roomToDelete.order_room_occupants?.length || 0}):</strong></Typography>
              {roomToDelete.order_room_occupants && roomToDelete.order_room_occupants.length > 0 ? (
                <Box sx={{ ml: 2, mt: 1 }}>
                  {roomToDelete.order_room_occupants.map((occupant, index) => (
                    <Typography key={index} variant="body2" sx={{ fontSize: '0.875rem' }}>
                      • {occupant.firstName} {occupant.lastName}
                    </Typography>
                  ))}
                </Box>
              ) : (
                <Typography variant="body2" sx={{ ml: 2, fontStyle: 'italic', color: 'text.secondary' }}>
                  No occupants
                </Typography>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDeleteDialog} disabled={deletingRoom}>
            Cancel
          </Button>
          <Button 
            onClick={handleConfirmDelete} 
            color="error" 
            variant="contained"
            disabled={deletingRoom}
          >
            {deletingRoom ? <CircularProgress size={20} /> : 'Delete Room'}
          </Button>
        </DialogActions>
      </Dialog>
      
      {/* Edit Contact Dialog */}
      {order && (
        <EditOrderContactDialog
          open={editContactDialogOpen}
          onClose={() => {
            setEditContactDialogOpen(false);
            // Refresh order data after edit
            fetchOrder();
          }}
          orderId={order.documentId}
        />
      )}

      {/* Edit Billing Dialog */}
      {order && (
        <EditOrderBillingDialog
          open={editBillingDialogOpen}
          onClose={() => {
            setEditBillingDialogOpen(false);
            // Refresh order data after edit
            fetchOrder();
          }}
          orderId={order.documentId}
        />
      )}

      {/* Room Menu */}
      <Menu
        anchorEl={roomMenuAnchor}
        open={Boolean(roomMenuAnchor) && selectedRoomIndex !== null}
        onClose={handleRoomMenuClose}
        PaperProps={{
          sx: {
            minWidth: 160
          }
        }}
      >
        {selectedRoomIndex !== null && order?.order_rooms?.[selectedRoomIndex] && (
          <>
            <MenuItem 
              onClick={() => handleEditRoom(order.order_rooms![selectedRoomIndex])}
              sx={{ gap: 1 }}
            >
              <EditIcon fontSize="small" />
              Edit Room
            </MenuItem>
            <MenuItem 
              onClick={() => handleAddOccupant(order.order_rooms![selectedRoomIndex])}
              sx={{ gap: 1 }}
            >
              <PersonAddIcon fontSize="small" />
              Add Occupant
            </MenuItem>
            <MenuItem 
              onClick={() => handleDeleteRoomFromMenu(order.order_rooms![selectedRoomIndex])}
              sx={{ gap: 1, color: 'error.main' }}
            >
              <DeleteIcon fontSize="small" />
              Delete Room
            </MenuItem>
          </>
        )}
      </Menu>

      {/* Edit Occupant Dialog */}
      {selectedRoom && (
        <EditOrderRoomOccupantDialog
          open={editOccupantDialogOpen}
          onClose={() => {
            setEditOccupantDialogOpen(false);
            setSelectedOccupant(null);
            setSelectedRoom(null);
          }}
          occupant={selectedOccupant}
          orderRoomId={selectedRoom.documentId || selectedRoom.id}
          onSave={() => {
            setEditOccupantDialogOpen(false);
            setSelectedOccupant(null);
            setSelectedRoom(null);
            // Refresh order data after edit
            fetchOrder();
          }}
        />
      )}

      {/* Edit Room Dialog */}
      {editingRoomId && (
        <EditRoomDialog
          open={editRoomDialogOpen}
          onClose={() => {
            setEditRoomDialogOpen(false);
            setEditingRoomId(null);
          }}
          roomId={editingRoomId}
          onSave={() => {
            setEditRoomDialogOpen(false);
            setEditingRoomId(null);
            // Refresh order data after edit
            fetchOrder();
          }}
        />
      )}
      
    </Grid>
  );
}